import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button, Card, Field, StatusPill, Modal } from "@/components/kit";

function AddRep({ onAdd }) {
  const [q, setQ] = useState("");
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2 || q.includes("@")) { setPeople([]); return; }
    const t = setTimeout(() => api.get("/membership/directory", { params: { tab: "residents", q, limit: 6 } })
      .then((r) => setPeople(r.data.members)).catch(() => setPeople([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-2" data-testid="rep-add">
      <Field label="Add a representative" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a person, or type an email" data-testid="rep-add-input" />
      {people.map((p) => (
        <button key={p.member_id} onClick={() => { onAdd({ person_member_id: p.member_id }); setQ(""); }} data-testid={`rep-pick-${p.member_number}`}
          className="w-full text-left bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px] hover:border-maroon/40 transition-colors">
          <span className="font-semibold">{p.name}</span> <span className="text-ink/55">{p.member_number} · {p.email}</span>
        </button>
      ))}
      {q.includes("@") && <Button onClick={() => { onAdd({ email: q }); setQ(""); }} data-testid="rep-add-email-btn">Add {q.trim()}</Button>}
    </div>
  );
}

export default function Representatives({ memberId }) {
  const [reps, setReps] = useState(null);
  const [invite, setInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [removing, setRemoving] = useState(null);
  const load = useCallback(() => api.get(`/membership/members/${memberId}/representatives`).then((r) => setReps(r.data.representatives)), [memberId]);
  useEffect(() => { load().catch(() => setReps([])); }, [load]);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); await load(); return true; } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); return false; } };
  const add = (body) => run(() => api.post(`/membership/members/${memberId}/representatives`, body), "Representative added");
  const remove = async () => { if (await run(() => api.post(`/membership/members/${memberId}/representatives/remove`, { key: removing.key }), "Representative removed")) setRemoving(null); };
  const send = async () => { if (await run(() => api.post(`/membership/members/${memberId}/representatives/invite`, { email }), "Invite sent")) { setInvite(false); setEmail(""); } };
  return (
    <Card className="!p-5" data-testid="representatives-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-zilla text-lg font-semibold">Representatives</h3>
        <Button variant="secondary" onClick={() => setInvite(true)} data-testid="rep-invite-btn">Invite by email</Button>
      </div>
      <div className="space-y-2 mb-4">
        {reps?.length === 0 && <div className="text-[14px] text-ink/55">No representatives yet.</div>}
        {reps?.map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-3 border border-ink/10 rounded-lg px-3 py-2" data-testid={`rep-row-${r.email}`}>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold truncate">{r.name || r.email}</div>
              <div className="text-[13px] text-ink/60 truncate">{r.email}{r.invited_at ? ` · invited ${r.invited_at.slice(0, 10)}` : ""}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {r.pending && <StatusPill tone="gold">Pending sign-in</StatusPill>}
              <button onClick={() => setRemoving(r)} className="text-maroon text-[13px] font-semibold hover:underline" data-testid={`rep-remove-${r.email}`}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      <AddRep onAdd={add} />
      <Modal open={invite} onClose={() => setInvite(false)} title="Invite a representative" data-testid="rep-invite-modal"
        footer={<><Button variant="secondary" onClick={() => setInvite(false)}>Cancel</Button><Button onClick={send} data-testid="rep-invite-send">Send invite</Button></>}>
        <p className="text-[14px] text-ink/70 mb-3">One email is sent now. When they sign in or register with this address, they're linked automatically at no charge.</p>
        <Field label="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="rep-invite-email" />
      </Modal>
      <Modal open={!!removing} onClose={() => setRemoving(null)} title="Remove representative?" data-testid="rep-remove-modal"
        footer={<><Button variant="secondary" onClick={() => setRemoving(null)}>Cancel</Button><Button onClick={remove} data-testid="rep-remove-confirm">Remove link</Button></>}>
        <p className="text-[14px] text-ink/70">This only removes the link. {removing?.name || removing?.email}'s login and own membership are not touched.</p>
      </Modal>
    </Card>
  );
}
